import { z } from 'zod';
import {
  AuditFieldsSchema,
  MoneyHufSchema,
  TenantIdSchema,
  TimestampSchema,
} from '../types/common.types';
import { PaginationQuerySchema } from '../types/pagination.types';

// ──────────────────────────────────────────────────────────────────────────
// Csomag-template contract (ADR-133 D-3 + ADR-118 Am.2 + ADR-108)
//
// A `Package` Prisma-modell a csomag-DEFINÍCIÓ (template) — ezt példányosítja a
// bérlés-varázsló 2. képernyője (`PackageSelection` audit). Ez a contract a
// sync-pont Javo (aggregate + contract) ↔ Peti (csomag-szerkesztő frontend) közt:
// a bérlés-varázsló package-selection step (ADR-103 Am-2) CSAK működő csomag-
// template-infrán épül (ADR-133 D-2 build-order invariáns).
//
// Tükrözi a `apps/kgc-api/prisma/schema/product.prisma` `Package` + `PackageItem`
// modelleket. Additív — nem érint létező schemát. Runtime aggregate (rental-equipment)
// Sprint 27 W2-W3 (ADR-133 D-2); ez a contract az early-unblock deliverable.
// ──────────────────────────────────────────────────────────────────────────

/** Csomag-típus (modulonkénti besorolás) — ADR-118 cardinality: RENTAL/SERVICE 1:1, SALES M:N. */
export const PackageTypeSchema = z.enum([
  'RENTAL', // Bérlési csomag (bérgép + tartozékok)
  'SALES', // Értékesítési csomag (termékek együtt)
  'SERVICE', // Szerviz csomag (karbantartási szolgáltatások)
  'MIXED', // Vegyes (bérlés + értékesítés cross-sell)
  'PROMOTIONAL', // Akciós/szezonális csomag
  'FRANCHISE', // Franchise induló készlet
]);
export type PackageType = z.infer<typeof PackageTypeSchema>;

/** Hatókör — CENTRAL: minden franchise látja; LOCAL: csak az adott telephely. */
export const PackageScopeSchema = z.enum(['CENTRAL', 'LOCAL']);
export type PackageScope = z.infer<typeof PackageScopeSchema>;

/** Árazási modell. TIERED = bérlés-hossztól függő (napi/heti/havi). */
export const PackagePricingModelSchema = z.enum(['FIXED', 'DISCOUNTED', 'TIERED']);
export type PackagePricingModel = z.infer<typeof PackagePricingModelSchema>;

/** Hatókör-mód: kategória-alapú VAGY konkrét gépmodell-alapú (PackageLinkedProduct join). */
export const PackageLinkingModeSchema = z.enum(['CATEGORY', 'EQUIPMENT']);
export type PackageLinkingMode = z.infer<typeof PackageLinkingModeSchema>;

/** Csomag-tétel típus (polimorf: PRODUCT VAGY RENTAL_EQUIPMENT FK). */
export const PackageItemTypeSchema = z.enum([
  'PRODUCT', // Termék/alkatrész (products tábla)
  'RENTAL_EQUIPMENT', // Bérgép (rental_equipment tábla)
  'ACCESSORY', // Tartozék
  'SERVICE_ITEM', // Szerviz szolgáltatás
]);
export type PackageItemType = z.infer<typeof PackageItemTypeSchema>;

/** Elszámolás — INCLUDED: ingyen tartozék; RENTAL: napi díj; CONSIGNMENT: bizomány. */
export const PackageItemSettlementSchema = z.enum(['INCLUDED', 'RENTAL', 'CONSIGNMENT']);
export type PackageItemSettlement = z.infer<typeof PackageItemSettlementSchema>;

/** Csomag-tétel besorolás (ADR-118 Am.2 D-1). FREE/RENTABLE/CONSIGNMENT. */
export const PackageItemCategorySchema = z.enum(['FREE', 'RENTABLE', 'CONSIGNMENT']);
export type PackageItemCategory = z.infer<typeof PackageItemCategorySchema>;

/**
 * itemType ↔ FK konzisztencia — a `@kgc/packages` `package-validation.service`
 * kanonikus szabálya (consumer-erős garancia a csomag-szerkesztőnek):
 *   - PRODUCT / ACCESSORY / SERVICE_ITEM → productId KÖTELEZŐ, rentalEquipmentId TILOS
 *   - RENTAL_EQUIPMENT                   → rentalEquipmentId KÖTELEZŐ, productId TILOS
 */
function itemTypeFkConsistent(i: {
  itemType: PackageItemType;
  productId?: string | null | undefined;
  rentalEquipmentId?: string | null | undefined;
}): boolean {
  if (i.itemType === 'RENTAL_EQUIPMENT') {
    return !!i.rentalEquipmentId && !i.productId;
  }
  return !!i.productId && !i.rentalEquipmentId;
}

const ITEM_FK_MESSAGE =
  'itemType↔FK: RENTAL_EQUIPMENT→rentalEquipmentId kötelező (productId tilos); PRODUCT/ACCESSORY/SERVICE_ITEM→productId kötelező (rentalEquipmentId tilos)';

/**
 * Csomag-tétel — polimorf (`productId` VAGY `rentalEquipmentId`, az itemType dönti
 * el melyik kötelező). A `unitPrice` null = a termék/gép saját ára.
 */
export const PackageItemSchema = z
  .object({
    id: z.string().uuid(),
    packageId: z.string().uuid(),
    itemType: PackageItemTypeSchema,
    settlement: PackageItemSettlementSchema,
    sortOrder: z.number().int().nonnegative().default(0),
    // Polimorf FK — legfeljebb az egyik (lásd refine alább)
    productId: z.string().uuid().nullable(),
    rentalEquipmentId: z.string().uuid().nullable(),
    quantity: z.number().int().positive().default(1),
    isOptional: z.boolean().default(false),
    isSubstitutable: z.boolean().default(false),
    isReturnable: z.boolean().default(true),
    unitPrice: MoneyHufSchema.nullable(),
    notes: z.string().max(500).nullable(),
    // ADR-118 Am.2 D-1 — csomag-tétel editor mezők (STORY-1205)
    category: PackageItemCategorySchema.default('RENTABLE'),
    defaultQuantity: z.number().int().positive().default(1),
    isQuantityEditable: z.boolean().default(false),
    visibleOnWebshop: z.boolean().default(false),
  })
  .refine(itemTypeFkConsistent, { message: ITEM_FK_MESSAGE, path: ['rentalEquipmentId'] });
export type PackageItem = z.infer<typeof PackageItemSchema>;

/** Csomag-template (definíció) — a `Package` aggregate gyökér. */
export const PackageTemplateSchema = z
  .object({
    id: z.string().uuid(),
    tenantId: TenantIdSchema,
    packageCode: z.string().min(1).max(50),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    type: PackageTypeSchema,
    scope: PackageScopeSchema.default('LOCAL'),
    pricingModel: PackagePricingModelSchema.default('FIXED'),
    // Árazás (modellfüggő; TIERED → napi/heti/havi, FIXED → fixedPrice)
    fixedPrice: MoneyHufSchema.nullable(),
    discountPercent: z.number().min(0).max(100).nullable(),
    dailyRate: MoneyHufSchema.nullable(),
    weeklyRate: MoneyHufSchema.nullable(),
    monthlyRate: MoneyHufSchema.nullable(),
    depositAmount: MoneyHufSchema.nullable(),
    // Érvényesség (szezonális akció)
    validFrom: TimestampSchema.nullable(),
    validTo: TimestampSchema.nullable(),
    isActive: z.boolean().default(true),
    // Hatókör (CATEGORY-mód: linkedEquipmentCategories; EQUIPMENT-mód: linkedProductIds)
    linkedEquipmentCategories: z.array(z.string()).default([]),
    linkedProductIds: z.array(z.string().uuid()).default([]),
    linkingMode: PackageLinkingModeSchema.default('CATEGORY'),
    // Kép
    imageUrl: z.string().url().nullable(),
    thumbnailUrl: z.string().url().nullable(),
    /**
     * Versioning-hook (ADR-133 D-1: package-versioning + fork-detect dialog).
     * Additív, opcionális — a server tölti, ha a versioning-infra él (Sprint 27 W2-W3).
     * A fork-detect (futó bérlés alatti template-változás) erre épül, NEM Phase 1-blokkoló.
     */
    version: z.number().int().positive().optional(),
    items: z.array(PackageItemSchema).default([]),
  })
  .merge(AuditFieldsSchema);
export type PackageTemplate = z.infer<typeof PackageTemplateSchema>;

/** Csomag-tétel létrehozás/szerkesztés DTO (admin csomag-szerkesztő). */
export const PackageItemCreateSchema = z
  .object({
    itemType: PackageItemTypeSchema,
    settlement: PackageItemSettlementSchema.default('INCLUDED'),
    sortOrder: z.number().int().nonnegative().default(0),
    productId: z.string().uuid().nullable().optional(),
    rentalEquipmentId: z.string().uuid().nullable().optional(),
    quantity: z.number().int().positive().default(1),
    isOptional: z.boolean().default(false),
    isSubstitutable: z.boolean().default(false),
    isReturnable: z.boolean().default(true),
    unitPrice: MoneyHufSchema.nullable().optional(),
    notes: z.string().max(500).nullable().optional(),
    category: PackageItemCategorySchema.default('RENTABLE'),
    defaultQuantity: z.number().int().positive().default(1),
    isQuantityEditable: z.boolean().default(false),
    visibleOnWebshop: z.boolean().default(false),
  })
  .refine(itemTypeFkConsistent, { message: ITEM_FK_MESSAGE, path: ['rentalEquipmentId'] });
export type PackageItemCreate = z.infer<typeof PackageItemCreateSchema>;

/**
 * Csomag-template létrehozás DTO — a szerver tölti id/tenantId/audit/version-t.
 * A backend `CreatePackageDto`-hoz igazítva: a csomag-fej jön létre itt, a TÉTELEK
 * KÜLÖN endpoint-on (`POST /packages/:id/items`, lásd `PackageItemCreateSchema`) —
 * ezért NINCS `items` mező a create-ben. EQUIPMENT-módban a `linkedProductIds`.
 */
export const PackageTemplateCreateSchema = z.object({
  packageCode: z.string().min(1).max(50),
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  type: PackageTypeSchema,
  scope: PackageScopeSchema.default('LOCAL'),
  pricingModel: PackagePricingModelSchema.default('FIXED'),
  fixedPrice: MoneyHufSchema.nullable().optional(),
  discountPercent: z.number().min(0).max(100).nullable().optional(),
  dailyRate: MoneyHufSchema.nullable().optional(),
  weeklyRate: MoneyHufSchema.nullable().optional(),
  monthlyRate: MoneyHufSchema.nullable().optional(),
  depositAmount: MoneyHufSchema.nullable().optional(),
  validFrom: TimestampSchema.nullable().optional(),
  validTo: TimestampSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  linkedEquipmentCategories: z.array(z.string()).default([]),
  linkedProductIds: z.array(z.string().uuid()).default([]),
  linkingMode: PackageLinkingModeSchema.default('CATEGORY'),
  imageUrl: z.string().url().nullable().optional(),
  thumbnailUrl: z.string().url().nullable().optional(),
});
export type PackageTemplateCreate = z.infer<typeof PackageTemplateCreateSchema>;

/** Csomag-template módosítás DTO (PATCH — minden mező opcionális). */
export const PackageTemplateUpdateSchema = PackageTemplateCreateSchema.partial();
export type PackageTemplateUpdate = z.infer<typeof PackageTemplateUpdateSchema>;

/**
 * Query-string boolean — `z.coerce.boolean()` HIBÁS query-paramra: `Boolean('false')`
 * truthy → `?isActive=false` fordítva szűrne (Peti #156 review-finding). Explicit
 * string→boolean transform helyette (zod 3.25 nincs `z.stringbool`).
 */
const QueryBoolSchema = z
  .enum(['true', 'false', '1', '0'])
  .transform(v => v === 'true' || v === '1');

/** Admin csomag-lista lekérdezés (szűrők + lapozás). */
export const PackageTemplateListQuerySchema = PaginationQuerySchema.extend({
  type: PackageTypeSchema.optional(),
  scope: PackageScopeSchema.optional(),
  isActive: QueryBoolSchema.optional(),
  search: z.string().optional(),
});
export type PackageTemplateListQuery = z.infer<typeof PackageTemplateListQuerySchema>;
